import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CardComp, TabsComp } from "../../../components";
import PageWrapper from "../modules/PageWrapper";
import LoanRepaymentTable from "./LoanRepaymentTable";
import { MemberLoanRepaymentHeader } from "../../../utils/TableHeader";
import LoanManagementCard from "../modules/LoanManagementCard";
import { useUser } from "../../../hooks";
import {
  useGetDueRepaymentLoanListQuery,
  useGetOverdueRepaymentLoanListQuery,
  useGetUpcomingRepaymentLoanListQuery,
  useGetMemberLoanSummaryQuery,
} from "../../../store/member/member-loan";
import moment from "moment";
import MemberLoanRepaymentReceiptModal from "./modals/MemberLoanRepaymentReceiptModal";
import MemberPendingBankTransfersCard from "../modules/MemberPendingBankTransfersCard";

export default function MemberLoanRepayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const [dueRepaymentPage, setDueRepaymentPage] = useState(1);
  const [upcomingRepaymentPage, setUpcomingRepaymentPage] = useState(1);
  const [overdueRepaymentPage, setOverdueRepaymentPage] = useState(1);

  // Fetch loan summary from backend (backend calculates counts)
  const { data: loanSummary, isLoading: isLoadingLoanSummary } = useGetMemberLoanSummaryQuery();

  const { data: upcomingRepayments, isLoading: isLoadingUpcomingRepayments } =
    useGetUpcomingRepaymentLoanListQuery({
      page: upcomingRepaymentPage,
      member_id: user?.member_id,
    });

  const { data: dueRepayments, isLoading: isLoadingDueRepayments } =
    useGetDueRepaymentLoanListQuery({
      page: dueRepaymentPage,
      member_id: user?.member_id,
    });

  const { data: overdueRepayments, isLoading: isLoadingOverdueRepayments } =
    useGetOverdueRepaymentLoanListQuery({
      page: overdueRepaymentPage,
      member_id: user?.member_id,
    });

  // Sort repayments by due date (earliest first)
  // Using the same field that the table header uses: due_at || next_due_date
  const sortedUpcomingRepayments = useMemo(() => {
    const repayments = upcomingRepayments?.data?.company_loan_requests || [];
    return [...repayments].sort((a, b) => {
      // Get due date using the same logic as the table header
      const dueDateA = a?.due_at || a?.next_due_date || a?.next_repayment;
      const dueDateB = b?.due_at || b?.next_due_date || b?.next_repayment;
      
      if (!dueDateA && !dueDateB) return 0;
      if (!dueDateA) return 1; // Items without due dates go to the end
      if (!dueDateB) return -1;
      
      // Sort ascending: earliest due date first
      return moment(dueDateA).diff(moment(dueDateB));
    });
  }, [upcomingRepayments]);

  const sortedDueRepayments = useMemo(() => {
    const repayments = dueRepayments?.data?.company_loan_requests || [];
    return [...repayments].sort((a, b) => {
      const dueDateA = a?.due_at || a?.next_due_date || a?.next_repayment;
      const dueDateB = b?.due_at || b?.next_due_date || b?.next_repayment;
      
      if (!dueDateA && !dueDateB) return 0;
      if (!dueDateA) return 1;
      if (!dueDateB) return -1;
      
      return moment(dueDateA).diff(moment(dueDateB));
    });
  }, [dueRepayments]);

  const sortedOverdueRepayments = useMemo(() => {
    const repayments = overdueRepayments?.data?.company_loan_requests || [];
    return [...repayments].sort((a, b) => {
      const dueDateA = a?.due_at || a?.next_due_date || a?.next_repayment;
      const dueDateB = b?.due_at || b?.next_due_date || b?.next_repayment;
      
      if (!dueDateA && !dueDateB) return 0;
      if (!dueDateA) return 1;
      if (!dueDateB) return -1;
      
      // For overdue, also sort ascending (oldest overdue first)
      return moment(dueDateA).diff(moment(dueDateB));
    });
  }, [overdueRepayments]);

  const cardData = [
    {
      name: `You have ${loanSummary?.overdue_repayment?.count || 0} overdue repayment${
        (loanSummary?.overdue_repayment?.count || 0) !== 1 ? "s" : ""
      }`,
      contributions: "Overdue Repayment",
      image: "/MemberSide/Icons/dashboard3.svg",
    },
    {
      name: `You have ${loanSummary?.upcoming_repayment?.count || 0} upcoming repayment${
        (loanSummary?.upcoming_repayment?.count || 0) !== 1 ? "s" : ""
      }`,
      contributions: "Upcoming Repayment",
      image: "/MemberSide/Icons/dashboard5.svg",
    },
  ];

  const CardSkeleton = () => (
    <div className=" space-x-4 rounded-[0.625rem] shadow3 flex items-center px-[2.42rem] py-[1.25rem] ">
      <div
        style={{
          width: "2.5rem",
          height: "2.5rem",
          borderRadius: "9999px",
          background: "#f1f1f1",
        }}
      />
      <div className="w-full space-y-2 ">
        <div
          style={{
            height: "14px",
            width: "40%",
            background: "#e5e5e5",
            borderRadius: "6px",
          }}
        />
        <div
          style={{
            height: "12px",
            width: "70%",
            background: "#f1f1f1",
            borderRadius: "6px",
          }}
        />
      </div>
    </div>
  );

  const [modalPage, setModalPage] = useState("");
  const [selectedRepaymentId, setSelectedRepaymentId] = useState(null);

  // Debug: Log modal state changes
  useEffect(() => {
    if (modalPage === "View Payment Receipt" && selectedRepaymentId) {
      console.log("Modal should be visible:", { modalPage, selectedRepaymentId });
    }
  }, [modalPage, selectedRepaymentId]);

  // Dynamic actions based on repayment status
  const getTableDropDown = (status) => {
    const actions = [];
    
    // Normalize status to uppercase for comparison
    const normalizedStatus = (status || "").toUpperCase();
    
    if (normalizedStatus === "PAID" || normalizedStatus === "COMPLETED") {
      actions.push({
        name: "View payment receipt",
        action: (data) => {
          console.log("View receipt clicked", data); // Debug log
          if (data?.id) {
            setSelectedRepaymentId(data.id);
            setModalPage("View Payment Receipt");
          } else {
            console.error("No repayment ID found in data:", data);
          }
        },
      });
    } else {
      actions.push({
        name: "Pay now",
        action: (data) => {
          // Extract loan_request_id from the repayment data
          const loanRequestId = data?.loan_request_id || data?.loan_request?.id;
          if (loanRequestId) {
            navigate(`${location?.pathname}/pay/${data?.id}?loan_request_id=${loanRequestId}`);
          } else {
            navigate(`${location?.pathname}/pay/${data?.id}`);
          }
        },
      });
    }
    
    return actions;
  };
  const useTab = [
    {
      name: "Upcoming repayment",
      component: (
        <LoanRepaymentTable
          name="upcoming repayment"
          TableData={sortedUpcomingRepayments}
          pagination={upcomingRepayments?.data?.pagination}
          loading={isLoadingUpcomingRepayments}
          setPageNumber={setUpcomingRepaymentPage}
          column={MemberLoanRepaymentHeader(getTableDropDown)}
        />
      ),
    },
    {
      name: "Due repayment",
      component: (
        <LoanRepaymentTable
          name="due repayment"
          TableData={sortedDueRepayments}
          pagination={dueRepayments?.data?.pagination}
          loading={isLoadingDueRepayments}
          setPageNumber={setDueRepaymentPage}
          column={MemberLoanRepaymentHeader(getTableDropDown)}
        />
      ),
    },
    {
      name: "Overdue repayment",
      component: (
        <LoanRepaymentTable
          name="overdue repayment"
          TableData={sortedOverdueRepayments}
          pagination={overdueRepayments?.data?.pagination}
          loading={isLoadingOverdueRepayments}
          setPageNumber={setOverdueRepaymentPage}
          column={MemberLoanRepaymentHeader(getTableDropDown)}
        />
      ),
    },
  ];

  return (
    <PageWrapper title={"Loan Repayment"} back>
      <section className="mt-6 space-y-6 ">
        <section className="mt-6 space-y-6 ">
          <div className="w-full flex justify-between items-center flex-wrap gap-12">
            <div className="flex-1">
              <LoanManagementCard />
            </div>
            <section className=" space-y-5 flex-1">
              {isLoadingLoanSummary ? (
                <>
                  <CardSkeleton />
                  <CardSkeleton />
                </>
              ) : (
                cardData?.map((item, i) => (
                  <div
                    className=" space-x-4 rounded-[0.625rem] shadow3 flex items-center px-[2.42rem] py-[1.25rem] "
                    key={i}
                  >
                    <img
                      src={item?.image}
                      alt="img"
                      className="w-[2.5rem] h-[2.5rem]"
                    />

                    <div className="w-full space-y-2 ">
                      <h4 className="">{item?.contributions}</h4>
                      <p
                        className={`text-muted h50 font-1 ${item?.nameClassName}`}
                      >
                        {item?.name}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </section>
          </div>
        </section>

        <MemberPendingBankTransfersCard flowFilter="loan" />

        <section className="space-y-4">
          <h4 className="font-bold text-base text-[#45464E] pt-[3rem] ">
            Loan Repayment History
          </h4>
          <CardComp cardClassName={"border-0 pt-[2rem] px-3"}>
            <div>
              <TabsComp
                TabArray={useTab}
                input="name"
                justifyContent={"justify-content-start"}
                tabClassName="px-0"
                TabArrayClassName="px-4"
              />
            </div>
          </CardComp>
        </section>
      </section>

      {/* Payment Receipt Modal */}
      {modalPage === "View Payment Receipt" && selectedRepaymentId && (
        <MemberLoanRepaymentReceiptModal
          repaymentId={selectedRepaymentId}
          setModalPage={setModalPage}
        />
      )}
    </PageWrapper>
  );
}
